
import {
  defineComponent,
  computed,
} from 'vue';

import { useStore } from 'vuex';

import { useRouter } from 'vue-router';

import type { MenuItem } from '@/types';

import {
  USER_TYPE_SUPER_ADMIN,
  USER_TYPE_ADMIN,
  USER_TYPE_MANAGER,
  USER_TYPES_MAP,
  PROJECT_ID_DEMO,
  THEME_DARK,
  THEME_LIGHT,
} from '@/libs/consts';

import useProject from '@/composable/useProject';

import useUser from '@/composable/useUser';

import TopBarMenu from '@/components/TopBarMenu.vue';

export default defineComponent({
  components: {
    TopBarMenu,
  },
  setup(props, { emit }) {
    const store = useStore();

    const router = useRouter();

    const themeIsLight = computed((): boolean => store.getters.themeIsLight);

    const themeIsDark = computed((): boolean => store.getters.themeIsDark);

    const { activeProject: project } = useProject();

    const { activeUser: user } = useUser();

    const menuItemsCommonTop: MenuItem[] = [
      {
        translate_key: 'dashboard',
        icon: 'dashboard',
        to: '/',
      },
    ];

    const menuItemsCommonBottom: MenuItem[] = [
      {
        translate_key: 'profile',
        icon: 'user',
        to: '/profile',
      },
      {
        translate_key: 'support',
        icon: 'people-safe',
        to: '/support',
      },
      {
        translate_key: 'api',
        icon: 'api',
        to: '/api',
      },
    ];

    const menuItemsStaff: MenuItem[] = [
      {
        translate_key: 'statistics',
        icon: 'chart-histogram-two',
        to: '/statistics/staff',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'statistics',
        icon: 'data-screen',
        to: '/statistics/staff-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'users',
        icon: 'every-user',
        to: '/users',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'sites',
        icon: 'page',
        to: '/sites',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'endpoints',
        icon: 'link-in',
        to: '/endpoints',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'feeds',
        icon: 'file-quality-one',
        to: '/feeds',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'discrepancies',
        icon: 'percentage',
        to: '/discrepancies',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
      },
      {
        translate_key: 'feeds_creatives',
        icon: 'picture',
        to: '/feeds-creatives',
        access: [
          USER_TYPE_SUPER_ADMIN,
        ],
      },
      {
        translate_key: 'Live Feeds Creatives',
        icon: 'picture',
        to: '/live-feeds-creatives',
        access: [
          USER_TYPE_SUPER_ADMIN,
        ],
      },
      {
        translate_key: 'creatives',
        icon: 'picture',
        to: '/creatives',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'creatives',
        icon: 'picture',
        to: '/creatives-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'campaigns',
        icon: 'newspaper-folding',
        to: '/campaigns',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'campaigns',
        icon: 'newspaper-folding',
        to: '/campaigns-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'landings',
        icon: 'page-template',
        to: '/landings-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'funnels',
        icon: 'filter',
        to: '/funnels-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'postback',
        icon: 'message-one',
        to: '/postback',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'postback',
        icon: 'message-one',
        to: '/postback-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'api_tokens',
        icon: 'key',
        to: '/api-tokens-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'adsecure',
        icon: 'report',
        to: '/adsecure',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'payouts',
        icon: 'funds',
        to: '/payouts',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'payments',
        icon: 'funds',
        to: '/payments',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'payments',
        icon: 'funds',
        to: '/payments-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'quality_presets',
        icon: 'tag',
        to: '/quality-presets',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
        ],
      },
      {
        translate_key: 'checker',
        icon: 'shield',
        to: '/checker',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.project_id === PROJECT_ID_DEMO,
      },
      {
        translate_key: 'checker',
        icon: 'shield',
        to: '/checker-next',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.project_id === PROJECT_ID_DEMO || user.isLimitedManager(),
        tagValue: 'Preview',
        tagSeverity: 'info',
      },
      {
        translate_key: 'publishers_activity_short',
        icon: 'link-in',
        to: '/activity/endpoints',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'feeds_activity_short',
        icon: 'file-quality-one',
        to: '/activity/feeds',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.isLimitedManager(),
      },
      {
        translate_key: 'advertisers_activity_short',
        icon: 'every-user',
        to: '/activity/advertisers',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'campaigns_activity_short',
        icon: 'newspaper-folding',
        to: '/activity/campaigns',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'projects',
        icon: 'death-star',
        to: '/projects',
        access: [
          USER_TYPE_SUPER_ADMIN,
        ],
      },
      {
        translate_key: 'project',
        icon: 'death-star',
        to: '/project',
        access: [
          USER_TYPE_ADMIN,
        ],
      },
      {
        translate_key: 'Domains',
        icon: 'page',
        to: '/domains',
        access: [
          USER_TYPE_SUPER_ADMIN,
        ],
      },
      {
        translate_key: 'pseudosites',
        icon: 'sorcerer-hat',
        to: '/pseudosites',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
        ],
      },
      {
        translate_key: 'schemes',
        icon: 'code-brackets',
        to: '/schemes',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
        ],
      },
      {
        translate_key: 'tools',
        icon: 'tool',
        to: '/tools',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
      },
      {
        translate_key: 'conflicts',
        icon: 'checklist',
        to: '/conflicts',
        access: [
          USER_TYPE_SUPER_ADMIN,
          USER_TYPE_ADMIN,
          USER_TYPE_MANAGER,
        ],
        hide: user.project_id === PROJECT_ID_DEMO || user.isLimitedManager(),
      },
      {
        translate_key: 'endpoints_efficiency',
        icon: 'percentage',
        to: '/endpoints-efficiency',
        access: [
          USER_TYPE_SUPER_ADMIN,
        ],
        hide: user.project_id === PROJECT_ID_DEMO,
      },
      {
        translate_key: 'chains_diff',
        icon: 'sinusoid',
        to: '/chains-diff',
        access: [
          USER_TYPE_SUPER_ADMIN,
        ],
      },
    ];

    const menuItemsPublisher = computed((): MenuItem[] => [
      {
        translate_key: 'statistics',
        icon: 'chart-histogram-two',
        to: '/statistics/publisher/direct',
        hide: !user.ui_publisher_direct,
      },
      {
        translate_key: 'rtb_statistics',
        icon: 'chart-histogram-two',
        to: '/statistics/publisher/rtb',
        hide: !user.ui_publisher_rtb,
      },
      {
        translate_key: 'sites',
        icon: 'page',
        to: '/sites',
        hide: !user.ui_publisher_direct,
      },
      {
        translate_key: 'endpoints',
        icon: 'link-in',
        to: '/endpoints',
      },
      {
        translate_key: 'payouts',
        icon: 'funds',
        to: '/payouts/direct',
        hide: !user.ui_publisher_direct,
      },
    ]);

    const menuItemsAdvertiser: MenuItem[] = [
      {
        translate_key: 'statistics',
        icon: 'chart-histogram-two',
        to: '/statistics/advertiser',
      },
      {
        translate_key: 'creatives',
        icon: 'picture',
        to: '/creatives',
      },
      {
        translate_key: 'campaigns',
        icon: 'newspaper-folding',
        to: '/campaigns',
      },
      {
        translate_key: 'postback',
        icon: 'message-one',
        to: '/postback',
      },
      {
        translate_key: 'payments',
        icon: 'funds',
        to: '/payments',
      },
    ];

    const menuItemsReferrer: MenuItem[] = [
      {
        translate_key: 'links',
        icon: 'link',
        to: '/links',
      },
      {
        translate_key: 'payouts',
        icon: 'funds',
        to: '/payouts/referrer',
      },
    ];

    const filteredMenuItemsStaff = menuItemsStaff.filter((menuItem: MenuItem) => menuItem.access?.includes(user.type));

    const onMenuButtonClick = (): void => {
      emit('onMenuButtonClick');
    };

    const toggleTheme = (): void => {
      const theme = store.getters.themeIsLight ? THEME_DARK : THEME_LIGHT;

      store.commit('SET_THEME', theme);
    };

    const logout = async (): Promise<void> => {
      store.commit('UNSET_TOKEN');

      await router.push('/login');
    };

    return {
      USER_TYPES_MAP,
      themeIsLight,
      themeIsDark,
      project,
      user,
      filteredMenuItemsStaff,
      menuItemsCommonTop,
      menuItemsCommonBottom,
      menuItemsPublisher,
      menuItemsAdvertiser,
      menuItemsReferrer,
      onMenuButtonClick,
      toggleTheme,
      logout,
    };
  },
});
